import { Link } from "react-router-dom";
import { BadgeCheck, ArrowRight, Check } from "lucide-react";
import { CommercialModule } from "@/components/site/CommercialModule";

const Heading = ({ children, id }) => (
  <h2 id={id} className="display text-[30px] md:text-[38px] leading-[1.15] text-charcoal mt-16 mb-6">
    {children}
  </h2>
);

const Paragraphs = ({ items }) => (
  <div className="prose-hhl">
    {items.map((p, i) => (
      <p key={i}>{p}</p>
    ))}
  </div>
);

export const SectionRenderer = ({ sections, articleSlug, shopLabel, offerAnchor, dedupeCategory }) => (
  <>
    {(sections || []).map((s, i) => {
      const id = `section-${i}`;
      switch (s.type) {
        case "prose":
          return (
            <section key={i} data-testid={`section-prose-${i}`}>
              {s.heading && <Heading id={id}>{s.heading}</Heading>}
              <Paragraphs items={s.paragraphs} />
            </section>
          );

        case "factors":
          return (
            <section key={i} data-testid={`section-factors-${i}`}>
              {s.heading && <Heading id={id}>{s.heading}</Heading>}
              {s.intro && <p className="prose-hhl">{s.intro}</p>}
              <div className="mt-2 divide-y divide-line border-t border-line">
                {s.items.map((it, j) => (
                  <div key={j} className="py-7 grid md:grid-cols-[1fr_2fr] gap-3 md:gap-8">
                    <h3 className="text-[17px] md:text-[18px] font-medium text-charcoal leading-snug">
                      {it.title}
                    </h3>
                    <p className="text-[16px] md:text-[17px] leading-[1.7] text-[#4A4A4A]">
                      {it.body}
                    </p>
                  </div>
                ))}
              </div>
            </section>
          );

        case "checklist":
          return (
            <section key={i} data-testid={`section-checklist-${i}`}>
              {s.heading && <Heading id={id}>{s.heading}</Heading>}
              {s.intro && <p className="prose-hhl">{s.intro}</p>}
              <ul className="mt-4 space-y-3.5">
                {s.items.map((it, j) => (
                  <li key={j} className="flex gap-3.5 items-start">
                    <Check size={17} className="text-sage-dark mt-1 shrink-0" />
                    <span className="text-[16px] md:text-[17px] leading-[1.65] text-[#4A4A4A]">
                      {it}
                    </span>
                  </li>
                ))}
              </ul>
            </section>
          );

        case "supporting":
          return (
            <section key={i} data-testid={`section-supporting-${i}`}>
              {s.heading && <Heading id={id}>{s.heading}</Heading>}
              <div className="divide-y divide-line border-y border-line">
                {s.items.map((it, j) => (
                  <div key={j} className="py-7">
                    <h3 className="text-[17.5px] md:text-[19px] font-medium text-charcoal mb-3">{it.q}</h3>
                    <p className="text-[16px] md:text-[17px] leading-[1.7] text-[#4A4A4A]">
                      {it.a}
                    </p>
                  </div>
                ))}
              </div>
            </section>
          );

        case "table":
          return (
            <section key={i} data-testid={`section-table-${i}`}>
              {s.heading && <Heading id={id}>{s.heading}</Heading>}
              <div className="overflow-x-auto border border-line bg-white">
                <table className="w-full text-left">
                  <thead>
                    <tr className="bg-bone">
                      {s.columns.map((c, j) => (
                        <th
                          key={j}
                          className="px-5 py-3.5 text-[10.5px] tracking-[0.16em] uppercase text-[#7A766F] font-medium border-b border-line"
                        >
                          {c}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {s.rows.map((r, j) => (
                      <tr key={j} className="border-b border-line/70 last:border-0">
                        {r.map((cell, k) => (
                          <td
                            key={k}
                            className={`px-5 py-4 text-[15px] leading-relaxed ${
                              k === 0 ? "text-charcoal" : "text-[#4A4A4A]"
                            }`}
                          >
                            {cell}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {s.note && <p className="text-[17px] md:text-[18px] leading-[1.5] text-[#3F3F3F] mt-4">{s.note}</p>}
            </section>
          );

        case "verified":
          if (s.needs_verification) return null;
          return (
            <aside
              key={i}
              data-testid={`section-verified-${i}`}
              className="my-14 bg-white border border-charcoal/85 px-6 md:px-9 py-8"
            >
              <div className="flex items-center gap-2.5 mb-4">
                <BadgeCheck size={15} className="text-charcoal" />
                <span className="text-[10px] tracking-[0.2em] uppercase text-charcoal font-medium">
                  Verified research
                </span>
              </div>
              <h3 className="display text-[24px] md:text-[27px] leading-snug text-charcoal mb-4">
                {s.heading}
              </h3>
              {s.paragraphs.map((p, j) => (
                <p key={j} className="text-[16px] md:text-[17px] leading-[1.7] text-[#3F3F3F] mb-4 last:mb-0">
                  {p}
                </p>
              ))}
            </aside>
          );

        case "tool":
          return (
            <aside
              key={i}
              data-testid={`section-tool-${i}`}
              className="my-14 bg-charcoal text-white px-6 md:px-9 py-9"
            >
              <span className="text-[10px] tracking-[0.2em] uppercase text-[#A9B8A2] font-medium">Planning tool</span>
              <h3 className="display text-[26px] md:text-[31px] leading-snug mt-3">{s.heading}</h3>
              <p className="text-[17px] md:text-[18px] leading-[1.5] text-[#C9C6C1] mt-4 max-w-xl">
                {s.note}
              </p>
              <Link
                to={`/tools/${s.tool_slug}`}
                data-testid={`section-tool-link-${s.tool_slug}`}
                className="inline-flex items-center gap-2 mt-6 px-7 py-3.5 bg-sage text-white text-[12.5px] tracking-[0.14em] uppercase hover:bg-white hover:text-charcoal transition-colors duration-300"
              >
                Open the tool <ArrowRight size={15} />
              </Link>
            </aside>
          );

        case "cta":
          // The guide template already carries one options pathway at the top and one in the
          // sidebar. A body CTA for that same category would be a third identical button, so it
          // is skipped: only a CTA for a different category still renders.
          if (dedupeCategory && s.offer_category === dedupeCategory) return null;
          return (
            <CommercialModule
              key={i}
              category={s.offer_category}
              label={shopLabel || s.label}
              headline={s.headline}
              note={s.note}
              placement={s.placement}
              articleSlug={articleSlug}
              anchorTo={offerAnchor}
            />
          );

        default:
          return null;
      }
    })}
  </>
);
